import { formatDateTimeRangeToISO } from "../../../../utils/dateTimeUtils";
import { WMSOrderFiltersType } from "../graphql/type/WMSOrderFiltersType";
import { SiteType } from "../../../../graphql/types/SiteType";
import { WarehouseData } from "../../../../contexts/WarehouseContext";

/**
 * 必填项归一化：用 currentWarehouse 兜底填充 currentWarehouseId / sortingCode。
 *
 * 抽取自 WmsParcelsPage.handleRequiredFields，保持行为一致，供「筛选」与「下载」共用，
 * 确保两个入口即使拿到的是未归一化的 tempFilters，也都带上仓库隔离锚点。
 */
export const normalizeRequiredFields = (
    filters: WMSOrderFiltersType,
    currentWarehouse?: WarehouseData | null
): WMSOrderFiltersType => {
    const warehouseId = currentWarehouse?.id || filters.currentWarehouseId;
    const hasCurrentWarehouseId = warehouseId && warehouseId !== "0";
    return {
        ...filters,
        currentWarehouseId: String(warehouseId || "0"),
        sortingCode: hasCurrentWarehouseId
            ? (filters.sortingCode || currentWarehouse?.sortingCode || "")
            : (filters.sortingCode || currentWarehouse?.sortingCode || "")
    };
};

/**
 * 构建包裹查询 ES 过滤器（不含 pageRequest）。
 *
 * 抽取自 useWmsParcelQuery.buildQueryVariables，作为「筛选」与「下载」两个入口唯一的
 * 参数构建逻辑，避免两份复制粘贴代码继续漂移。
 */
export const buildParcelQueryFilters = (
    filters: WMSOrderFiltersType,
    currentWarehouse?: WarehouseData | null
): any => {
    const sortingCodes = currentWarehouse?.sortingCode?.split('-') || [];
    const siteType = currentWarehouse?.siteType;

    const timeRanges = {
        orderTime: formatDateTimeRangeToISO(filters.orderTime),
        receivedAtHub: formatDateTimeRangeToISO(filters.receivedAtHub),
        destHubArrivedAt: formatDateTimeRangeToISO(filters.destHubArrivedAt),
        destWarehouseArrivedAt: formatDateTimeRangeToISO(filters.destWarehouseArrivedAt),
        deliverAt: formatDateTimeRangeToISO(filters.deliverAt),
        markExceptionAt: formatDateTimeRangeToISO(filters.markExceptionAt),
        deliveredAt: formatDateTimeRangeToISO(filters.deliveredAt),
        lastDeliveryScanTime: formatDateTimeRangeToISO(filters.lastDeliveryScanTime),
        updatedAt: formatDateTimeRangeToISO(filters.updatedAt),
        returnArrivalHubAt: formatDateTimeRangeToISO(filters.returnArrivalHubAt)
    };

    // 判断用户是否手动选择了分拣码段
    const hasUserSelectedSortingCodeSegment = filters.sortingCodeSegment1 != null ||
                                              filters.sortingCodeSegment2 != null ||
                                              filters.sortingCodeSegment3 != null;

    // 判断是否有 currentWarehouseId
    const hasCurrentWarehouseId = filters.currentWarehouseId && filters.currentWarehouseId !== "0";
    // 构建分拣码段过滤器实现数据隔离
    // 如果用户手动选择了分拣码段，优先使用用户选择的值
    // 如果有 currentWarehouseId，不自动提取分拣码段，使用 sortingCode 查询（让后端走 OR 逻辑路径）
    // 只有在没有 currentWarehouseId 时，才从 currentWarehouse 提取分拣码段实现数据隔离
    const sortingFilters: Partial<WMSOrderFiltersType> = {};
    if (!hasUserSelectedSortingCodeSegment && !hasCurrentWarehouseId && sortingCodes.length > 0) {
        // 用户没有手动选择分拣码段，且没有 currentWarehouseId 时，才从 currentWarehouse 提取分拣码段
        sortingFilters.sortingCodeSegment1 = sortingCodes[0] || undefined;
        if (sortingCodes.length > 1) {
            sortingFilters.sortingCodeSegment2 = sortingCodes[1] || undefined;
            if (sortingCodes.length > 2) {
                sortingFilters.sortingCodeSegment3 = sortingCodes[2] || undefined;
            }
        }
    }

    // 必填项封装
    const requiredFilters: Partial<WMSOrderFiltersType> = {};
    requiredFilters.currentWarehouseId = filters.currentWarehouseId;
    // 只有在没有提供分拣码段筛选条件时，才使用完整的 sortingCode
    // 如果用户通过一段码和二段码筛选，应该使用分拣码段而不是完整的 sortingCode
    // 如果有 currentWarehouseId，只使用用户手动选择的分拣码段；如果没有 currentWarehouseId，使用手动选择或自动提取的分拣码段
    const finalSortingCodeSegment1 = hasCurrentWarehouseId
        ? filters.sortingCodeSegment1
        : (filters.sortingCodeSegment1 || sortingFilters.sortingCodeSegment1);
    const finalSortingCodeSegment2 = hasCurrentWarehouseId
        ? filters.sortingCodeSegment2
        : (filters.sortingCodeSegment2 || sortingFilters.sortingCodeSegment2);
    const finalSortingCodeSegment3 = hasCurrentWarehouseId
        ? filters.sortingCodeSegment3
        : (filters.sortingCodeSegment3 || sortingFilters.sortingCodeSegment3);
    if (!finalSortingCodeSegment1 && !finalSortingCodeSegment2 && !finalSortingCodeSegment3) {
        if (!filters.sortingCodeSegment1 && !filters.sortingCodeSegment2 && !filters.sortingCodeSegment3) {
            // 如果有 currentWarehouseId，使用 currentWarehouse.sortingCode 作为 sortingCode（查询在本站点的包裹）
            // 后端会查询当前在本仓、应揽收仓、在本站点的包裹
            // 如果没有 currentWarehouseId，使用 filters.sortingCode
            requiredFilters.sortingCode = hasCurrentWarehouseId
                ? (filters.sortingCode || currentWarehouse?.sortingCode || "")
                : filters.sortingCode;
        }
    }

    // 处理 receiverZipcode：如果有原始输入，转换为数组（与 trackingNumbers 一致）
    let receiverZipcode: string[] | undefined = filters.receiverZipcode;
    if (filters.receiverZipcodeInput) {
        // 支持换行符、半角逗号和空格分隔（多个空格会被视为一个分隔符）
        const zipcodes = filters.receiverZipcodeInput
            .split(/[\r\n,\s]+/)  // 按换行符、逗号或空格分割（\s+ 匹配一个或多个空白字符）
            .map(z => z.trim())
            .filter(z => z.length > 0);
        receiverZipcode = zipcodes.length > 0 ? zipcodes : undefined;
    }

    // 构建查询过滤器，避免单值和多值字段冲突
    // 如果用户手动选择了分拣码段，需要清空 filters 中已有的 hubId/warehouseId
    const baseFilters = hasUserSelectedSortingCodeSegment
        ? { ...filters, hubId: undefined, warehouseId: undefined }
        : filters;

    const queryFilters: any = {
        ...baseFilters,
        ...timeRanges,
        ...requiredFilters,
        // 转换数组类型为多值查询
        channelIds: filters.channelId?.map(id => parseInt(id)) || undefined,
        pickupHubIds: filters.pickupHubId?.map(id => parseInt(id)) || undefined,
        overallStatuses: filters.overallStatus || undefined,
        nextActions: (filters.nextActions && filters.nextActions.length > 0) ? filters.nextActions : undefined,
        deliveredDspIds: (filters.deliveredDspIds && filters.deliveredDspIds.length > 0) ? filters.deliveredDspIds : undefined,
        receiverZipcode: receiverZipcode, // 使用转换后的数组
        pickupTaskDate: undefined, // 不发送到后端
        // 如果用户手动选择了分拣码段，不设置 hubId/warehouseId，只使用分拣码段查询（支持查询新建订单）
        // 如果没有手动选择分拣码段，强制设置仓库相关过滤实现数据隔离
        hubId: hasUserSelectedSortingCodeSegment ? undefined : (String(siteType) === String(SiteType.HUB) ? currentWarehouse?.id : currentWarehouse?.parentSiteId),
        warehouseId: hasUserSelectedSortingCodeSegment ? undefined : (String(siteType) === String(SiteType.WAREHOUSE) ? currentWarehouse?.id : undefined),
        // 关键：添加分拣码段过滤
        // 如果有 currentWarehouseId，只使用用户手动选择的分拣码段，不自动提取（让后端走 sortingCode 的 OR 逻辑路径）
        // 如果没有 currentWarehouseId，使用用户手动选择的分拣码段或从 currentWarehouse 提取的分拣码段
        sortingCodeSegment1: hasCurrentWarehouseId
            ? filters.sortingCodeSegment1
            : (filters.sortingCodeSegment1 || sortingFilters.sortingCodeSegment1 || undefined),
        sortingCodeSegment2: hasCurrentWarehouseId
            ? filters.sortingCodeSegment2
            : (filters.sortingCodeSegment2 || sortingFilters.sortingCodeSegment2 || undefined),
        sortingCodeSegment3: hasCurrentWarehouseId
            ? filters.sortingCodeSegment3
            : (filters.sortingCodeSegment3 || sortingFilters.sortingCodeSegment3 || undefined),
    };

    if (hasUserSelectedSortingCodeSegment) {
        if (queryFilters.currentWarehouseId) {
            queryFilters.currentWarehouseId = typeof queryFilters.currentWarehouseId === 'string'
                ? parseInt(queryFilters.currentWarehouseId)
                : (typeof queryFilters.currentWarehouseId === 'number'
                    ? queryFilters.currentWarehouseId
                    : parseInt(String(queryFilters.currentWarehouseId)));
        } else if (hasCurrentWarehouseId && currentWarehouse?.id) {
            // 如果没有 currentWarehouseId，但 hasCurrentWarehouseId 为 true，使用 currentWarehouse.id
            queryFilters.currentWarehouseId = typeof currentWarehouse.id === 'string'
                ? parseInt(currentWarehouse.id)
                : (typeof currentWarehouse.id === 'number'
                    ? currentWarehouse.id
                    : parseInt(String(currentWarehouse.id)));
        } else {
            // 如果没有 currentWarehouseId，设置为 0（默认值）
            queryFilters.currentWarehouseId = 0;
        }

        if (hasCurrentWarehouseId || currentWarehouse?.id) {
            // 如果有 currentWarehouseId，使用 currentWarehouse.sortingCode 作为 sortingCode
            // 后端会先应用三个包裹范围的前提条件（OR逻辑），然后再应用分拣码段筛选条件（AND逻辑）
            if (!queryFilters.sortingCode && currentWarehouse?.sortingCode) {
                queryFilters.sortingCode = currentWarehouse.sortingCode;
            } else if (!queryFilters.sortingCode) {
                queryFilters.sortingCode = "";
            }
        }

        // 确保 sortingCode 有有效值（GraphQL schema 要求非空）
        if (!queryFilters.sortingCode) {
            queryFilters.sortingCode = "";
        }
    } else {
        // 用户没有选择分拣码段，确保 currentWarehouseId 和 sortingCode 都有有效值（GraphQL schema 要求非空）
        // currentWarehouseId 需要转换为数字类型（Long）
        if (queryFilters.currentWarehouseId) {
            queryFilters.currentWarehouseId = typeof queryFilters.currentWarehouseId === 'string'
                ? parseInt(queryFilters.currentWarehouseId)
                : (typeof queryFilters.currentWarehouseId === 'number'
                    ? queryFilters.currentWarehouseId
                    : parseInt(String(queryFilters.currentWarehouseId)));
        } else {
            // 如果没有 currentWarehouseId，设置为 0（默认值）
            queryFilters.currentWarehouseId = 0;
        }

        if (hasCurrentWarehouseId) {
            // 如果有 currentWarehouseId，使用 currentWarehouse.sortingCode 作为 sortingCode
            // 后端会使用 sortingCode 进行数据隔离（AND条件），确保只查询属于当前站点的包裹
            if (!queryFilters.sortingCode && currentWarehouse?.sortingCode) {
                queryFilters.sortingCode = currentWarehouse.sortingCode;
            } else if (!queryFilters.sortingCode) {
                queryFilters.sortingCode = "";
            }
        }

        // 确保 sortingCode 有有效值（GraphQL schema 要求非空）
        if (!queryFilters.sortingCode) {
            queryFilters.sortingCode = "";
        }
    }

    // 清空临时输入字段，不发送到后端
    queryFilters.receiverZipcodeInput = undefined;

    // 清空单值字段，避免冲突
    queryFilters.channelId = undefined;
    queryFilters.pickupHubId = undefined;
    queryFilters.overallStatus = undefined;
    queryFilters.nextAction = undefined;
    queryFilters.deliveredDspId = undefined;

    // 移除无效的时间范围字段，避免GraphQL schema错误
    Object.keys(queryFilters).forEach(key => {
        const value = queryFilters[key];
        if (value === undefined) {
            delete queryFilters[key];
            return;
        }
        if (typeof value === 'object' && value !== null) {
            const hasStart = Object.prototype.hasOwnProperty.call(value, 'start');
            const hasEnd = Object.prototype.hasOwnProperty.call(value, 'end');
            if ((hasStart || hasEnd) && value.start === undefined && value.end === undefined) {
                delete queryFilters[key];
            }
        }
    });

    return queryFilters;
};
